import React from 'react';
import { Grid, Paper } from '@mui/material';

import ChatSessionsPanel from './ChatSessionsPanel';
import ChatMessageList from './ChatMessageList';
import ChatComposer from './ChatComposer';
import McpChatControls from './McpChatControls';
import { ID } from '../../../../types/aiTypes';
import { useAiChat } from '../../../../hooks/useAiChat';
import { DEFAULT_AI_MODEL_VALUE } from '../../../../support/aiModelOptions';

type Props = {
  embedded?: boolean;
  height?: string | number;
  showSessions?: boolean;
  related_type?: string;
  related_id?: ID;
  defaultUseDrift?: boolean;
  defaultUseMcp?: boolean;
};

const AiChat: React.FC<Props> = ({
                                   embedded = false,
                                   height = '72vh',
                                   showSessions = true,
                                   related_type,
                                   related_id,
                                   defaultUseDrift = true,
                                   defaultUseMcp = true,
                                 }) => {
  const [ragProfileId, setRagProfileId] = React.useState<number | null>(null);
  const [selectedRagProfile, setSelectedRagProfile] = React.useState<any | null>(null);
  const [modelName, setModelName] = React.useState<string>(DEFAULT_AI_MODEL_VALUE);

  const chat = useAiChat({
    related_type,
    related_id,
    defaultUseDrift,
    defaultUseMcp,

    // RAG profile / pipeline group selectors.
    rag_profile_id: ragProfileId,
    pipeline_group_id: ragProfileId,

    // LLM selector. Multiple aliases are intentionally sent for compatibility
    // with existing hook/backend naming conventions.
    model_name: modelName,
    modelName,
    llm_model: modelName,
    llmModel: modelName,
  } as any) as any;

  const effectiveRagProfileId =
    chat.ragProfileId ??
    chat.pipelineGroupId ??
    ragProfileId ??
    null;

  const effectiveRagProfile =
    chat.ragProfile ??
    chat.pipelineGroup ??
    selectedRagProfile ??
    null;

  const effectiveModelName =
    chat.modelName ??
    chat.model_name ??
    chat.llmModel ??
    chat.llm_model ??
    modelName ??
    DEFAULT_AI_MODEL_VALUE;

  const changeRagProfile = (value: number | null, profile?: any | null) => {
    setRagProfileId(value);
    setSelectedRagProfile(profile || null);

    if (typeof chat.setRagProfileId === 'function') {
      chat.setRagProfileId(value, profile || null);
    }

    if (typeof chat.setPipelineGroupId === 'function') {
      chat.setPipelineGroupId(value, profile || null);
    }

    if (typeof chat.setRagProfile === 'function') {
      chat.setRagProfile(profile || null);
    }

    if (typeof chat.setPipelineGroup === 'function') {
      chat.setPipelineGroup(profile || null);
    }
  };

  const changeModelName = (value: string) => {
    setModelName(value);

    if (typeof chat.setModelName === 'function') {
      chat.setModelName(value);
    }

    if (typeof chat.setModel_name === 'function') {
      chat.setModel_name(value);
    }

    if (typeof chat.setLlmModel === 'function') {
      chat.setLlmModel(value);
    }

    if (typeof chat.setLlm_model === 'function') {
      chat.setLlm_model(value);
    }
  };

  const mainPanel = (
    <Paper
      elevation={embedded ? 0 : 1}
      sx={{
        p: 2,
        height,
        display: 'flex',
        flexDirection: 'column',
        border: embedded ? '1px solid' : undefined,
        borderColor: embedded ? 'divider' : undefined,
      }}
    >
      <ChatMessageList
        messages={chat.messages}
        toolTraces={chat.toolTraces}
        loading={chat.historyLoading}
      />

      <McpChatControls
        useDrift={chat.useDrift}
        onUseDriftChange={chat.setUseDrift}
        pipelineId={chat.pipelineId}
        ragProfileId={effectiveRagProfileId}
        selectedRagProfile={effectiveRagProfile}
        onRagProfileChange={changeRagProfile}
        modelName={effectiveModelName}
        onModelNameChange={changeModelName}
        useMcp={chat.useMcp}
        onUseMcpChange={chat.setUseMcp}
        forceRefresh={chat.forceMcpRefresh}
        onForceRefreshChange={chat.setForceMcpRefresh}
      />

      <ChatComposer
        value={chat.input}
        onChange={chat.setInput}
        onSend={chat.sendMessage}
        onCancel={chat.cancel}
        sending={chat.sending}
        streamStatus={chat.streamStatus}
      />
    </Paper>
  );

  if (!showSessions) return mainPanel;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={4} xl={3}>
        <ChatSessionsPanel
          sessions={chat.sessions}
          selectedSessionId={chat.selectedSessionId}
          onSelect={chat.setSelectedSessionId}
          onNew={chat.createNewChat}
          onRemove={chat.removeChat}
        />
      </Grid>

      <Grid item xs={12} lg={8} xl={9}>
        {mainPanel}
      </Grid>
    </Grid>
  );
};

export default AiChat;